import { noop } from "lodash";

class PromiseWithResolve<T> extends Promise<T> {
	public resolve: (value: T) => void;
	public reject: (error: unknown) => void;

	constructor(fn: (resolve: (value: T) => void, reject: (error: unknown) => void) => void) {
		let resolve, reject;
		super((res, rej) => {
			resolve = res;
			reject = rej;
			return fn(res, rej);
		});
		if (resolve && reject) {
			this.resolve = resolve;
			this.reject = reject;
		} else {
			// This should be unreachable
			throw new Error();
		}
	}
}

export class Lock {
	private waiters: PromiseWithResolve<void>[] = [];
	private locked = false;

	async acquire(): Promise<boolean> {
		if (!this.locked && this.waiters.length == 0) {
			this.locked = true;
			return true;
		}
		const fut = new PromiseWithResolve<void>(noop);
		this.waiters.push(fut);
		try {
			await fut;
		} finally {
			const idx = this.waiters.indexOf(fut);
			if (idx !== -1) {
				this.waiters.splice(idx, 1);
			}
		}
		this.locked = true;
		return true;
	}

	release() {
		if (this.locked) {
			this.locked = false;
			const waiter = this.waiters.shift();
			if (waiter) {
				waiter.resolve();
			}
		}
	}
}

interface Entry<T> {
	createdAt: number;
	value: T;
}

export default class Cache<K, V> {
	public readonly ttl: number;
	public readonly fetch: (key: K) => Promise<V>;
	private cache: Map<K, Entry<V>>;
	private locks: Map<K, Lock>;

	constructor(ttl: number, fetch: (key: K) => Promise<V>) {
		this.ttl = ttl;
		this.fetch = fetch;
		this.cache = new Map();
		this.locks = new Map();
	}

	set(key: K, value: V): Entry<V> {
		let entry;
		this.cache.set(
			key,
			(entry = {
				createdAt: Date.now(),
				value,
			}),
		);
		return entry;
	}

	async get(key: K): Promise<V> {
		let lock = this.locks.get(key);
		if (!lock) this.locks.set(key, (lock = new Lock()));
		await lock.acquire();
		let entry: Entry<V> | undefined;
		if (!(entry = this.cache.get(key)) || entry.createdAt < Date.now() - this.ttl) {
			entry = this.set(key, await this.fetch(key));
		}
		lock.release();
		return entry.value;
	}
}
